import { backendClient } from "../api/backend-client";
import { createAiEvent } from "../../utils/assistantUtils";

export class RealTimeCallClient {
    constructor() {
        this.isActive = false;
        this.peerConnection = null;
        this.dataChannel = null;
        this.messageCallback = null;
        this.callMetadata = null;
        this._activeSpeakingId = null;
        this._pendingTranscriptIds = [];
        this._currentResponseId = null;
        this._currentResponseText = '';

        this.sessionUsage = {
            totalTokens: 0,
            inputTokens: 0,
            outputTokens: 0,
            cachedTokens: 0,
            inputTextTokens: 0,
            inputAudioTokens: 0,
            outputTextTokens: 0,
            outputAudioTokens: 0,
            cachedTextTokens: 0,
            cachedAudioTokens: 0,
            transcriptionDurationSeconds: 0,
            transcriptionCount: 0,
            responseCount: 0,
            startTime: null,
            endTime: null
        };
    }
    // async start(remoteStream, sipPeerConnection, onMessage) {
    async start(remoteStream, onMessage) {
        this.isActive = true;
        this.messageCallback = onMessage;
        this.sessionUsage.startTime = Date.now();

        // Setup peer connection
        this.peerConnection = new RTCPeerConnection();

        // remote audio
        remoteStream.getTracks().forEach(t =>
            this.peerConnection.addTrack(t, remoteStream)
        );

        //  When OpenAI sends audio back, replace SIP local track
        // this.peerConnection.ontrack = (event) => {
        //     const aiTrack = event.streams[0]?.getAudioTracks()[0];
        //     if (!aiTrack || !sipPeerConnection) return;

        //     const sender = sipPeerConnection
        //         .getSenders()
        //         .find(s => s.track?.kind === 'audio');

        //     if (sender) {
        //         sender.replaceTrack(aiTrack);  // 👈 caller now hears OpenAI voice
        //         console.log('✅ Replaced SIP local track with OpenAI audio');
        //     }
        // };

        // Setup data channel
        this.dataChannel = this.peerConnection.createDataChannel('oai-events');
        this._setupDataChannelHandlers();

        // Negotiate with backend
        await this._negotiateConnection();
    }

    async stop() {
        this.dataChannel?.close();
        this.peerConnection?.close();

        this.isActive = false;
        this.messageCallback = null;
        this.peerConnection = null;
        this.dataChannel = null;

        this.sessionUsage.endTime = Date.now();
        await this._sendUsageToBackend();
    }



    setCallMetadata(metadata) {
        this.callMetadata = metadata;
    }

    sendMessage(text) {
        if (!this.dataChannel || this.dataChannel.readyState !== 'open') return;
        this.dataChannel.send(JSON.stringify({
            type: 'conversation.item.create',
            item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] }
        }));
        this.dataChannel.send(JSON.stringify({ type: 'response.create' }));
    }


    _setupDataChannelHandlers() {
        this.dataChannel.onopen = () => {
            console.log('✅ Data channel opened');

            // Trigger AI to speak first
            this.dataChannel.send(JSON.stringify({
                type: 'conversation.item.create',
                item: {
                    type: 'message',
                    role: 'user',
                    content: [{ type: 'input_text', text: '.' }]
                }
            }));

            this.dataChannel.send(JSON.stringify({
                type: 'response.create'
            }));
        };

        this.dataChannel.onmessage = (evt) => {
            const msg = JSON.parse(evt.data);
            this._handleDataChannelMessage(msg);
        };
    }

    _handleDataChannelMessage(msg) {
        switch (msg.type) {
            case "session.created":
                this.messageCallback?.(
                    createAiEvent("system", "AI session started")
                );
                break;

            case "input_audio_buffer.speech_started": {
                const id = crypto.randomUUID();
                this._activeSpeakingId = id;
                this._pendingTranscriptIds.push(id);
                this.messageCallback?.(createAiEvent("customer_speech", null, id, "speaking"));
                break;
            }

            case "input_audio_buffer.speech_stopped": {
                if (this._activeSpeakingId) {
                    this.messageCallback?.({
                        ...createAiEvent("customer_speech", null, this._activeSpeakingId, "transcribing"),
                        isReplace: true,
                    });
                    this._activeSpeakingId = null;
                }
                break;
            }

            case "conversation.item.input_audio_transcription.completed": {
                console.log("[VERBOSE] 📊 Transcription usage:", msg);
                const id = this._pendingTranscriptIds.shift() ?? crypto.randomUUID();
                this.messageCallback?.({
                    ...createAiEvent("customer_speech", msg.transcript, id, "done"),
                    isReplace: true,
                });
                if (msg.usage?.type === 'duration') {
                    this.sessionUsage.transcriptionDurationSeconds += msg.usage.seconds || 0;
                    this.sessionUsage.transcriptionCount += 1;
                }
                break;
            }

            case "response.output_text.delta": {
                if (!this._currentResponseId) {
                    this._currentResponseId = crypto.randomUUID();
                    this._currentResponseText = msg.delta;
                    this.messageCallback?.(
                        createAiEvent("agent_response", msg.delta, this._currentResponseId)
                    );
                } else {
                    this._currentResponseText += msg.delta;
                    this.messageCallback?.({
                        ...createAiEvent("agent_response", this._currentResponseText, this._currentResponseId),
                        isReplace: true,
                    });
                }
                break;
            }

            case "response.output_text.done": {
                if (this._currentResponseId) {
                    this.messageCallback?.({
                        ...createAiEvent("agent_response", msg.text, this._currentResponseId),
                        isReplace: true,
                    });
                    this._currentResponseId = null;
                    this._currentResponseText = '';
                }
                break;
            }

            // case "response.output_audio_transcript.done":
            //     this.messageCallback?.(
            //         createAiEvent("agent_response", msg.transcript)  // msg.transcript not msg.text
            //     );
            //     break;

            case "response.done":
                console.log("[VERBOSE] 📊 Response usage:", msg.response.usage);
                this._accumulateUsage(msg.response.usage);
                break;

            case "error":
                this.messageCallback?.(
                    createAiEvent("error", msg.error?.message || "Unknown error")
                );
                break;

            default:
                break;
        }
    }

    async _negotiateConnection() {
        const offer = await this.peerConnection.createOffer();
        await this.peerConnection.setLocalDescription(offer);

        const sdpRes = await backendClient.post(
            'realtime/session/audio',
            { sdp: offer.sdp, phoneNumber: this.callMetadata?.callNumber, crm: this.callMetadata?.crm },
            { headers: { 'Content-Type': 'application/json' } }
        );

        const { sdp, lead } = sdpRes.data;

        // Emit to UI immediately via callback
        if (lead) {
            this.messageCallback?.(createAiEvent('lead_context', lead));
        }

        await this.peerConnection.setRemoteDescription({
            type: 'answer',
            sdp: sdp
        });
    }


    _accumulateUsage(usage) {
        if (!usage) return;

        this.sessionUsage.responseCount += 1;
        this.sessionUsage.totalTokens += usage.total_tokens || 0;
        this.sessionUsage.inputTokens += usage.input_tokens || 0;
        this.sessionUsage.outputTokens += usage.output_tokens || 0;

        // Input token details
        if (usage.input_token_details) {
            this.sessionUsage.inputTextTokens += usage.input_token_details.text_tokens || 0;
            this.sessionUsage.inputAudioTokens += usage.input_token_details.audio_tokens || 0;
            this.sessionUsage.cachedTokens += usage.input_token_details.cached_tokens || 0;

            // Cached token details
            if (usage.input_token_details.cached_tokens_details) {
                this.sessionUsage.cachedTextTokens += usage.input_token_details.cached_tokens_details.text_tokens || 0;
                this.sessionUsage.cachedAudioTokens += usage.input_token_details.cached_tokens_details.audio_tokens || 0;
            }
        }

        // Output token details
        if (usage.output_token_details) {
            this.sessionUsage.outputTextTokens += usage.output_token_details.text_tokens || 0;
            this.sessionUsage.outputAudioTokens += usage.output_token_details.audio_tokens || 0;
        }

        console.log("[VERBOSE] 📊 Accumulated session usage:", this.sessionUsage);
    }

    async _sendUsageToBackend() {
        if (this.sessionUsage.responseCount === 0 && this.sessionUsage.transcriptionCount === 0) {
            console.log("ℹ️ No usage to report (session had no activity)");
            return;
        }


        try {
            const duration = this.sessionUsage.endTime - this.sessionUsage.startTime;

            const usageData = {
                ...this.sessionUsage,
                sessionDuration: Math.floor(duration / 1000), // in seconds
                callId: this.callMetadata?.callId,
                callNumber: this.callMetadata?.callNumber,
                callDirection: this.callMetadata?.callDirection,
                displayName: this.callMetadata?.displayName,
            };

            console.log("[VERBOSE] 📤 Sending usage to backend:", usageData);

            await backendClient.post('/realtime/usage/audio', usageData);

            console.log("✅ Usage data sent successfully");

            // reset to prevent retry loops
            this.sessionUsage.responseCount = 0;
            this.sessionUsage.transcriptionCount = 0;

        } catch (error) {
            console.error("❌ Failed to send usage data:", error);

            // reset to prevent retry loops
            this.sessionUsage.responseCount = 0;
            this.sessionUsage.transcriptionCount = 0;

        }
    }
}